import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Info } from "lucide-react";

const API = "/api";

const money = (amount, currency) => {
  const symbols = { PEN: "S/ ", INR: "₹", USD: "$ ", EUR: "€ ", ARS: "$ ", BRL: "R$ " };
  return `${symbols[currency] || `${currency} `}${Number(amount).toFixed(2)}`;
};

const GROUPS = [
  ["by_month", "Por mes"],
  ["by_plan", "Por plan"],
  ["by_country", "Por país"],
  ["by_method", "Por método de cobro"],
];

/** MASTER ADMIN V2 §1 — ingresos operativos: solo pagos APROBADOS, sin mezclar monedas. */
export const RevenuePanel = ({ hideTest = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState("by_month");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/business/revenue`, {
        params: hideTest ? { hide_test: "1" } : { hide_test: "0" },
      });
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hideTest]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center rounded-xl border border-white/10 bg-[#0B2545] py-12">
        <Loader2 className="h-5 w-5 animate-spin text-[#5BC0FF]" />
      </div>
    );
  }
  if (!data) return null;

  const rows = data[group] || [];

  return (
    <div className="rounded-xl border border-white/10 bg-[#0B2545] p-5" data-testid="admin-revenue-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-semibold text-white md:text-lg">
          <TrendingUp className="h-4 w-4 text-emerald-300" aria-hidden="true" />
          Ingresos cobrados
        </h2>
        <div className="flex flex-wrap gap-1.5">
          {GROUPS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setGroup(key)}
              className={`rounded-full px-3 py-1 text-xs transition-colors ${
                group === key
                  ? "bg-[#1B84FF]/20 text-sky-200 ring-1 ring-inset ring-[#1B84FF]/40"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`revenue-group-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-4" data-testid="revenue-totals">
        {data.totals.length === 0 ? (
          <p className="text-sm text-slate-400">Todavía no hay ningún pago aprobado en este rango.</p>
        ) : (
          data.totals.map((t) => (
            <div key={t.currency} className="rounded-xl bg-emerald-500/10 px-4 py-3 ring-1 ring-inset ring-emerald-400/25"
                 data-testid={`revenue-total-${t.currency}`}>
              <p className="text-[10px] uppercase tracking-wide text-emerald-300/80">{t.currency}</p>
              <p className="text-2xl font-bold text-white">{money(t.amount, t.currency)}</p>
              <p className="text-[11px] text-emerald-200/80">{t.count} pago(s) aprobado(s)</p>
            </div>
          ))
        )}
      </div>

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="revenue-breakdown-table">
            <thead>
              <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-4">Grupo</th>
                <th className="py-2 pr-4">Moneda</th>
                <th className="py-2 pr-4">Pagos</th>
                <th className="py-2">Cobrado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.key}-${r.currency}`} className="border-b border-white/5">
                  <td className="py-2 pr-4 text-white">{r.key}</td>
                  <td className="py-2 pr-4 text-slate-400">{r.currency}</td>
                  <td className="py-2 pr-4 text-slate-300">{r.count}</td>
                  <td className="py-2 font-medium text-white">{money(r.amount, r.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-lg bg-black/25 p-3 text-[11px] leading-relaxed text-slate-400">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-500" aria-hidden="true" />
        <div data-testid="revenue-provenance">
          <p>{data.basis}</p>
          <p>{data.currency_policy}</p>
          <p>{data.net_warning}</p>
          <p className="text-slate-500">
            Rango: {data.range} · {data.hide_test ? "sin datos de prueba" : "incluye datos de prueba"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RevenuePanel;
